/**
 * /portal/tasks/new (T-039 form mode + T-040 chat mode).
 *
 * Renders inside PortalLayout. Resolves the caller's subscriber id + access
 * token (needed for the shape-check Authorization header per ARCH-009), then
 * hands off to TaskIntake. Operators viewing the portal have no subscriber
 * row, so intake is disabled for them with an explanation (the operator
 * authors tasks via the operator dashboard / MCP, not this form).
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { getCurrentSession } from '@/lib/auth';
import { fetchOwnSubscriber } from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { TaskIntake } from '@/components/portal/TaskIntake';

export const Route = createFileRoute('/portal/tasks/new')({
  component: NewTaskPage,
});

interface IntakeBootstrap {
  subscriberId: string;
  tier: 'studio' | 'pro';
  accessToken: string;
}

function NewTaskPage() {
  const { portal } = Route.useRouteContext();
  const [state, setState] = useState<AsyncState<IntakeBootstrap | null>>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    if (portal.isOperator) {
      setState({ status: 'ready', data: null });
      return;
    }
    Promise.all([getCurrentSession(), fetchOwnSubscriber()])
      .then(([session, sub]) => {
        if (cancelled) return;
        if (!session || !sub) {
          setState({
            status: 'error',
            message:
              'Could not load your account. Refresh, or sign in again if it persists.',
          });
          return;
        }
        setState({
          status: 'ready',
          data: {
            subscriberId: sub.id,
            tier: sub.tier === 'pro' ? 'pro' : 'studio',
            accessToken: session.access_token,
          },
        });
      })
      .catch(() => {
        if (!cancelled)
          setState({
            status: 'error',
            message: 'Could not load the task form. Try again in a moment.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [portal.isOperator]);

  return (
    <AsyncPanel state={state} loadingLabel="Loading the task form...">
      {(boot) =>
        boot === null ? (
          <div className="max-w-2xl space-y-3">
            <h1 className="font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
              New task
            </h1>
            <p className="text-sm text-[color:var(--color-text-mute)]">
              You are viewing the portal as an operator. Task intake is for
              subscribers; author tasks from the operator dashboard.
            </p>
          </div>
        ) : (
          <TaskIntake
            subscriberId={boot.subscriberId}
            tier={boot.tier}
            accessToken={boot.accessToken}
          />
        )
      }
    </AsyncPanel>
  );
}
